// synthetic-arabic: reviewed-no-scripture — the Arabic here is ordinary product interface wording invented for this application; it contains no Qur'an, hadith, translation or religious quotation.
import { useEffect, useRef, useState } from 'react';
import {
  awaitOperation,
  createApiClient,
  type FetchLike,
  type MemoryFactDto,
  type SessionSummaryDto,
  type TurnResponseDto,
  type ReadinessDto,
  type TurnInput,
} from './api/client.js';
import { AssistantResponse } from './components/AssistantResponse.js';
import type { PlayAudio } from './components/ListenButton.js';
import type { SpokenAudio } from './voice/spoken-audio.js';
import { analysedBrowserAudioPlayer, playRecitationUrl } from './voice/analysed-player.js';
import {
  startLiveTranscript,
  type LiveTranscriptSeams,
  type LiveTranscriptSession,
} from './voice/live-transcript.js';
import { currentSession } from './voice/server-stt.js';
import type { PressToTalk } from './voice/press-to-talk.js';
import { Sidebar, applyTheme, initialTheme, type Theme } from './components/Sidebar.js';
import { Composer, type DepthMode, type VoicePhase } from './components/Composer.js';
import { BilalMark } from './components/BilalMark.js';
import { EmptyExamples, EmptyGreeting } from './components/EmptyState.js';
import { VoiceMode } from './components/VoiceMode.js';
import { ExplorePanel } from './components/ExplorePanel.js';
import { InteractionPanel, type PlanStep } from './components/InteractionPanel.js';
// The application's own marker for an act rather than typed words (ADR-028).
const INTERACTION_ACT_MARKER = '[تفاعل]';
import { startVoiceLoop, type VoiceLoop, type VoiceLoopState } from './voice/voice-loop.js';
import {
  operationalMessage,
  RETRYABLE_DEGRADES,
  safetyText,
} from './components/AssistantResponse.js';
import { matchMediaOrNull } from './browser.js';
import { stageLine } from './stage-lines.js';
import { Onboarding } from './components/Onboarding.js';
import { LanguageSwitch } from './components/LanguageSwitch.js';
import { DetailsFields, statedDetailsOf, type DetailsValue } from './components/DetailsFields.js';
import type {
  AccountProfileDto,
  AgeRangeDto,
  CountryCodeDto,
  InterestDto,
  ReferralDto,
  SurveyDueDto,
} from './api/client.js';
import { SurveyForm } from './components/SurveyForm.js';
import { Settings } from './components/Settings.js';
import { preferences, type AnalyticsConsent } from './preferences.js';
import { NO_ANALYTICS, type Analytics } from './analytics.js';
import {
  LanguageContext,
  browserLanguages,
  directionOf,
  languageFor,
  type LanguageChoice,
} from './i18n.js';
import { MESSAGES } from './messages.js';
import { takeLandingCta } from './landing-arrival.js';

/** Non-production development build: synthetic data only, deterministic fakes, no real provider. */
export const DEVELOPMENT_INVITE = 'synthetic-development-invite-0001';

type Phase = 'signed_out' | 'ready' | 'sending';

/** Read through a function so an earlier `if (!mounted.current) return` cannot narrow it to `true`. */
function stillMounted(flag: { readonly current: boolean }): boolean {
  return flag.current;
}

function newClientTurnId(): string {
  const random = globalThis.crypto.getRandomValues(new Uint8Array(8));
  const tail = Array.from(random, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `00000000-0000-4000-8000-${tail.slice(0, 12)}`;
}

/** The closed choices as user traits (never the name or the email); null profile means none. */
function traitsOf(
  profile:
    | {
        readonly gender?: string;
        readonly ageRange?: string;
        readonly country?: string;
        readonly referral?: string;
        readonly interest?: string;
      }
    | null
    | undefined,
):
  | { gender?: string; ageRange?: string; country?: string; referral?: string; interest?: string }
  | undefined {
  if (profile === null || profile === undefined) return undefined;
  return {
    ...(profile.gender === undefined ? {} : { gender: profile.gender }),
    ...(profile.ageRange === undefined ? {} : { ageRange: profile.ageRange }),
    ...(profile.country === undefined ? {} : { country: profile.country }),
    ...(profile.referral === undefined ? {} : { referral: profile.referral }),
    ...(profile.interest === undefined ? {} : { interest: profile.interest }),
  };
}

export function App({
  fetchImpl,
  baseUrl = '',
  capture,
  player,
  liveSeams,
  analytics = NO_ANALYTICS,
}: {
  readonly fetchImpl: FetchLike;
  readonly baseUrl?: string;
  /**
   * Absent when the browser cannot record. Then no microphone control is offered at all, rather than
   * one that fails when pressed.
   */
  readonly capture?: PressToTalk | undefined;
  /** How delivered audio is played. Injected so the browser pieces stay out of the decisions here. */
  readonly player?: PlayAudio | undefined;
  /** Realtime capture seams; injected in tests, real browser pieces otherwise. */ readonly liveSeams?:
    LiveTranscriptSeams | undefined;
  /** Product analytics; inert unless configured at build time and consented to by the person. */
  readonly analytics?: Analytics | undefined;
}): React.JSX.Element {
  const api = createApiClient(baseUrl, fetchImpl);
  const playAudio = player ?? analysedBrowserAudioPlayer();
  const [phase, setPhase] = useState<Phase>('signed_out');
  /** The introduction, once per browser. A resumed session never meets it. */
  const [onboarded, setOnboarded] = useState<boolean>(() => preferences.onboardingDone());
  const [settingsOpen, setSettingsOpen] = useState(false);
  /**
   * The experience survey (founder, 2026-09-20). Whether it is due is the server's decision and is
   * asked after each delivered turn; while it is set the composer is blocked, which is what makes
   * it mandatory. The server never raises it on a conversation the Guard has closed.
   */
  const [survey, setSurvey] = useState<Extract<SurveyDueDto, { due: true }> | null>(null);
  /** Whether a released hold sends (when the transcription is sure) or always reviews. */
  const [voiceAutoSend, setVoiceAutoSendState] = useState<boolean>(() =>
    preferences.voiceAutoSend(),
  );
  const [analyticsConsent, setAnalyticsConsentState] = useState<AnalyticsConsent | null>(() =>
    preferences.analyticsConsent(),
  );
  /** Shown in the settings so the person knows which account this is; null until known. */
  const [accountEmail, setAccountEmail] = useState<string | null>(null);
  /** The name the person chose (ADR-034), for the greeting and the drawer; null until known. */
  const [accountName, setAccountName] = useState<string | null>(null);
  /** Once: which call to action on the welcome page brought the person here (ADR-036). */
  const reportLandingArrival = (): void => {
    const cta = takeLandingCta();
    if (cta !== null) analytics.track('landing_cta_clicked', { cta });
  };
  const chooseAnalyticsConsent = (consent: AnalyticsConsent): void => {
    preferences.setAnalyticsConsent(consent);
    setAnalyticsConsentState(consent);
    analytics.consentChanged(consent);
    if (consent === 'granted') reportLandingArrival();
  };
  const chooseVoiceAutoSend = (on: boolean): void => {
    preferences.setVoiceAutoSend(on);
    setVoiceAutoSendState(on);
    analytics.track('settings_changed', { setting: 'voice_auto_send', value: on });
  };
  /** Typed by the person at the door. A closed beta is governed by who holds a code. */
  const [inviteCode] = useState('');
  /** The same floor the API enforces; stated once so the two cannot drift apart. */
  const PASSWORD_MIN = 4;
  // Sign-up leads (founder, 2026-09-20: "SIGN UP should be first"): most people meeting this card
  // have no account, and the person who has one is one press from their own door.
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'reset'>('register');
  /** A forgotten password (redesign canvas): the address, then the code and a new password. */
  const [resetStep, setResetStep] = useState<'email' | 'code'>('email');
  const [authNotice, setAuthNotice] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [gender, setGender] = useState<'female' | 'male' | null>(null);
  /** The four closed statistics choices (founder, 2026-09-13), stated with the name and the gender. */
  const [ageRange, setAgeRange] = useState<AgeRangeDto | null>(null);
  const [country, setCountry] = useState<CountryCodeDto | null>(null);
  const [referral, setReferral] = useState<ReferralDto | null>(null);
  const [interest, setInterest] = useState<InterestDto | null>(null);
  const [accountUserId, setAccountUserId] = useState<string | null>(null);
  const detailsValue: DetailsValue = { displayName, gender, ageRange, country, referral, interest };
  const changeDetails = (next: DetailsValue): void => {
    setDisplayName(next.displayName);
    setGender(next.gender);
    setAgeRange(next.ageRange);
    setCountry(next.country);
    setReferral(next.referral);
    setInterest(next.interest);
  };
  /**
   * The account's stated details, as the server holds them. Without this the settings would open on
   * an empty form for anyone who signed in rather than registered, and saving it would blank what
   * they had already said. Absent fields stay unset rather than becoming a guess.
   */
  const hydrateDetails = (profile: AccountProfileDto | null | undefined): void => {
    if (profile === null || profile === undefined) return;
    changeDetails({
      displayName: profile.displayName,
      gender: profile.gender,
      ageRange: profile.ageRange ?? null,
      country: profile.country ?? null,
      referral: profile.referral ?? null,
      interest: profile.interest ?? null,
    });
  };
  /** Registration is two steps (ADR-034): the details, then the code sent to the address. */
  const [registrationStep, setRegistrationStep] = useState<'details' | 'code'>('details');
  /** The sign-in link (ADR-041): asked for from the door, or waiting here for one press. */
  const [linkStep, setLinkStep] = useState<'idle' | 'sent'>('idle');
  const [pendingLink, setPendingLink] = useState<string | null>(null);
  /** The card after the door: the two details Bilal needs, then the four it does not. */
  const [profileStep, setProfileStep] = useState<'identity' | 'statistics'>('identity');
  const [code, setCode] = useState('');
  const [resendAt, setResendAt] = useState<number | null>(null);
  const [, setResendTick] = useState(0);
  /** A signed-in account with an email and no stated details: asked once, before the conversation. */
  const [needsProfile, setNeedsProfile] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  // The survey is also asked whenever the conversation in hand changes — a reload, a resume, another
  // conversation opened from the rail — and not only after a reply. Asked only after a reply, a
  // person could reload with the form unanswered and have the composer back until the next reply
  // landed: one free message per reload, which is not what mandatory means. The server decides each
  // time, so opening a conversation that does not owe one simply clears it.
  useEffect(() => {
    if (sessionId === null) {
      setSurvey(null);
      return;
    }
    let cancelled = false;
    void api.surveyDue(sessionId).then((due) => {
      if (!cancelled) setSurvey(due.ok && due.value.due ? due.value : null);
    });
    return () => {
      cancelled = true;
    };
    // `api` is rebuilt every render from two stable props; the conversation is what this follows.
  }, [sessionId]);
  const [draft, setDraft] = useState('');
  /**
   * The conversation as the person sees it: their words and Bilal's replies, in order. The server holds
   * its own copy for the composer; this one exists to be read, so it renders and never re-sends.
   * An assistant entry keeps its operation id, because that id is the only thing the page may name when
   * asking for speech — which is what keeps text the page happens to be showing out of the request.
   */
  const [messages, setMessages] = useState<
    readonly (
      | {
          readonly role: 'user';
          readonly text: string;
          /** Written while an earlier turn was still running; it is sent when that one finishes. */
          readonly pending?: boolean;
          /** Chosen from an interaction rather than typed. Shown as a choice, never as their words. */
          readonly choice?: boolean;
        }
      | {
          readonly role: 'assistant';
          readonly turn: TurnResponseDto;
          /** Null for a turn rebuilt from the server's log after a refresh: its words survive, its audio does not. */
          readonly operationId: string | null;
        }
    )[]
  >([]);
  /** The visible profile: what Bilal saved. Saving is automatic; this is where it can be seen. */
  const [memory, setMemory] = useState<{ readonly facts: readonly MemoryFactDto[] }>({ facts: [] });
  const factsCount = useRef(0);
  /** The profile panel, opened from the rail — hidden until asked for, like every chat product. */
  const [memoryOpen, setMemoryOpen] = useState(false);
  const [exploreOpen, setExploreOpen] = useState(false);
  /** Index of the turn after which something was saved; renders the small saved chip there. */
  const [savedAfterTurn, setSavedAfterTurn] = useState<number | null>(null);
  /** The stage the server says is running right now; null whenever no turn is in flight. */
  const [stage, setStage] = useState<string | null>(null);
  /** Which delivered turn is being spoken and how loud the playback really is. */
  const [speaking, setSpeaking] = useState<{ readonly op: string; readonly level: number } | null>(
    null,
  );
  /** The realtime transcription in progress: what is committed, the one interim segment, and the
   *  real microphone level. Null whenever no live recording exists. */
  const [liveVoice, setLiveVoice] = useState<{
    readonly committed: string;
    readonly interim: string;
    readonly level: number;
    readonly startedAt: number;
    readonly live: boolean;
  } | null>(null);
  const liveSession = useRef<LiveTranscriptSession | null>(null);
  /** The live voice conversation, when one is open. */
  const [voiceMode, setVoiceMode] = useState<VoiceLoopState | null>(null);
  const voiceLoop = useRef<VoiceLoop | null>(null);

  const refreshMemory = async (): Promise<number> => {
    const facts = await api.listMemoryFacts();
    if (!facts.ok) return factsCount.current;
    setMemory({ facts: facts.value.facts });
    factsCount.current = facts.value.facts.length;
    return facts.value.facts.length;
  };

  /**
   * Messages written while a turn was in flight. The server takes one turn at a time by design — the
   * session state machine refuses a second — so the page holds them and sends them in order rather
   * than dropping them, which is what it used to do silently.
   */
  const [queue, setQueue] = useState<readonly TurnInput[]>([]);
  /** Set when a delivered turn ended the session; the queue may not be sent into a closed one. */
  const sessionEnded = useRef(false);
  const queueRef = useRef<readonly TurnInput[]>([]);
  queueRef.current = queue;

  const [error, setError] = useState<string | null>(null);
  /** The words an outage could not send, kept so one tap re-sends them rather than retyping. */
  const [retryable, setRetryable] = useState<string | null>(null);
  const [liveWiring, setLiveWiring] = useState<ReadinessDto | null>(null);
  // The hands-free conversation is offered only when the server says the capability is on. It
  // is off by default (founder, 2026-09-11): the path is not working well enough, and a button
  // that leads to a closed door is worse than no button. Recorded speech stays.
  const realtimeVoiceEnabled =
    liveWiring?.capabilities?.some((c) => c.capability === 'realtimeVoice' && c.enabled) === true;
  /** The caller's conversations, newest first, as the rail lists them. */
  const [conversations, setConversations] = useState<readonly SessionSummaryDto[]>([]);
  /**
   * What the running system says about the release its cards come from. Unknown stays `null`, which the
   * card treats as unreviewed: an approval this client cannot establish is never shown as one.
   */
  const [voice, setVoice] = useState<VoicePhase>('off');
  /**
   * عمق الإجابة: balanced for everyone since 2026-09-20 (founder: "use Balanced as default and hide
   * others"). The choice is gone from the composer and the settings; the field stays on the wire
   * because the server owns what a depth means and a later product decision may offer it again. A
   * value stored by someone who once chose quick or deep is deliberately not read, so nobody is left
   * on a depth with no control to leave it by.
   */
  const depth: DepthMode = 'balanced';
  /** The person's explicit theme choice; null follows the system. */
  const [theme, setTheme] = useState<Theme>(() => initialTheme());
  /**
   * The interface language. `auto` follows the browser, which is the default and the reason someone
   * whose phone is in English meets an English product without going looking for a setting.
   */
  const [languageChoice, setLanguageChoice] = useState<LanguageChoice>(() =>
    preferences.language(),
  );
  const language = languageFor(languageChoice, browserLanguages());
  const direction = directionOf(language);
  const t = MESSAGES[language];
  const chooseLanguage = (choice: LanguageChoice): void => {
    preferences.setLanguage(choice);
    setLanguageChoice(choice);
    analytics.track('settings_changed', { setting: 'language', value: choice });
  };
  // The document carries the language and the direction too: the scrollbar, the caret and every
  // date a browser formats follow the root element, not this component's own attributes.
  useEffect(() => {
    const root = globalThis.document.documentElement;
    root.setAttribute('lang', language);
    root.setAttribute('dir', direction);
  }, [language, direction]);
  /** The rail as a drawer on narrow screens. */
  const [railOpen, setRailOpen] = useState(false);
  /** The rail hidden on wide screens, the way every chat product lets the list fold away. */
  const [railCollapsed, setRailCollapsed] = useState(false);
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);
  const toggleTheme = (): void => {
    setTheme((current) => {
      if (current !== null) return current === 'dark' ? 'light' : 'dark';
      const systemDark = matchMediaOrNull?.('(prefers-color-scheme: dark)').matches ?? false;
      return systemDark ? 'light' : 'dark';
    });
  };
  /** The settings' three-way choice; null follows the system. */
  const chooseTheme = (next: Theme): void => {
    setTheme(next);
    analytics.track('settings_changed', { setting: 'theme', value: next ?? 'system' });
  };
  const speakButton = useRef<HTMLButtonElement>(null);
  const stopButton = useRef<HTMLButtonElement>(null);
  const memorySection = useRef<HTMLElement>(null);
  const composerBox = useRef<HTMLTextAreaElement>(null);
  const scrollEnd = useRef<HTMLLIElement>(null);
  /** Guards against a double Enter racing React state: at most one turn is ever in flight. */
  const inFlight = useRef(false);
  /**
   * Every press-to-talk control replaces itself when pressed, and a control that unmounts while it holds
   * focus drops the keyboard user back to the document body. The handoff is stated as an intent rather
   * than inferred from the phase, so it cannot race the preview claiming focus for itself.
   */
  const [focusIntent, setFocusIntent] = useState<'speak' | 'stop' | null>(null);
  useEffect(() => {
    if (focusIntent === null) return;
    if (focusIntent === 'speak') speakButton.current?.focus();
    else stopButton.current?.focus();
    setFocusIntent(null);
  }, [focusIntent]);

  // The newest words stay in view: whenever a turn is added or the reply is being composed, the list
  // follows to the end. Smoothness is the stylesheet's decision, so reduced motion is respected there.
  useEffect(() => {
    // jsdom implements no scrolling, so the node is viewed through a type that admits the absence;
    // following the conversation is a nicety, never a requirement.
    const end: { scrollIntoView?: (options: ScrollIntoViewOptions) => void } | null =
      scrollEnd.current;
    end?.scrollIntoView?.({ block: 'end' });
  }, [messages, phase]);

  // Whether real providers are wired is a fact about the running system, so it is read once and shown
  // whatever the sign-in state: someone must never mistake a live-provider build for the offline one.
  useEffect(() => {
    const mounted = { current: true };
    void (async () => {
      const ready = await api.readiness();
      if (!ready.ok || !mounted.current) return;
      setLiveWiring(ready.value);
    })();
    return () => {
      mounted.current = false;
    };
    // Readiness is a startup fact; the client is rebuilt per render but holds no state.
  }, []);

  // A page refresh keeps the session cookie, so try to resume before asking for a development sign-in.
  useEffect(() => {
    const mounted = { current: true };
    void (async () => {
      const existing = await api.currentSession();
      if (!existing.ok) return;
      // Resume what this caller already has; only start a new one when there is nothing to continue.
      const open = await api.resumeSession();
      const resumed = open.ok ? open : await api.createSession();
      // One guard, at the only point where it matters: never set state after unmount.
      if (!resumed.ok || !mounted.current) return;
      setSessionId(resumed.value.id);
      currentSession.id = resumed.value.id;
      // A refresh shows the words again: the server re-reads its own in-process log. Cards and
      // speech are not in the log; they reappear on new turns.
      const held = await api.conversation(resumed.value.id);
      if (held.ok && held.value.entries.length > 0) {
        setMessages(
          held.value.entries.map((entry) =>
            entry.role === 'user'
              ? {
                  role: 'user' as const,
                  text: entry.text,
                  // A rebuilt act is shown as a choice, exactly as it was when they made it.
                  ...(entry.text.startsWith(INTERACTION_ACT_MARKER) ? { choice: true } : {}),
                }
              : {
                  role: 'assistant' as const,
                  operationId: null,
                  turn: {
                    turnId: '00000000-0000-4000-8000-000000000000',
                    assistantProse: entry.text,
                    sourceLinkProse: entry.sourceLinkProse ?? null,
                    sourceCards: entry.sourceCards ?? [],
                    interaction: entry.interaction ?? null,
                    safetyAction: 'none' as const,
                    degradeReason: null,
                    notice: null,
                  },
                },
          ),
        );
      }
      setPhase('ready');
      void refreshConversations();
      // A resumed session knows which account it is only by asking.
      const me = await api.me();
      if (me.ok && stillMounted(mounted)) {
        setAccountEmail(me.value.email);
        setAccountName(me.value.profile?.displayName ?? null);
        setNeedsProfile(me.value.email !== null && me.value.profile === null);
        setAccountUserId(me.value.userId);
        hydrateDetails(me.value.profile);
        analytics.identify(me.value.userId, traitsOf(me.value.profile));
      }
    })();
    return () => {
      mounted.current = false;
    };
    // Resuming runs once on mount; the client is rebuilt per render but holds no state.
  }, []);

  // The page reads the return once, says it, and cleans the address bar. A successful return
  // carries no token — the session cookie is already set; the resume effect finds it.
  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    const code = params.get('oauth');
    if (code === null) return;
    globalThis.history.replaceState(null, '', globalThis.location.pathname);
    if (code === 'ok' || code === 'created') {
      analytics.track(code === 'created' ? 'signup_completed' : 'login', { method: 'google' });
      return;
    }
    setError(t.oauth[code] ?? t.failure.google);
    if (code === 'adult_attestation_required') setAuthMode('register');
    // Once, on mount: the address bar is read when the page opens.
  }, []);
  // A sign-in link (ADR-041) carries its token in the fragment, which no server ever sees. It is
  // taken out of the address bar at once — pressed or not — so it is in no history entry, no
  // screenshot and no copied URL.
  useEffect(() => {
    const readLink = (): void => {
      const match = /^#signin=([A-Za-z0-9_-]{32,200})$/u.exec(globalThis.location.hash);
      if (match?.[1] === undefined) return;
      const token = match[1];
      globalThis.history.replaceState(
        null,
        '',
        `${globalThis.location.pathname}${globalThis.location.search}`,
      );
      if (preferences.linkRequestedRecently()) void finishLink(token);
      else setPendingLink(token);
    };
    readLink();
    // A link pasted into a tab that already has the page open changes the fragment without
    // loading anything, so the page listens for that as well as reading it on arrival.
    globalThis.addEventListener('hashchange', readLink);
    return () => {
      globalThis.removeEventListener('hashchange', readLink);
    };
    // Once, on mount: the listener reads the address bar whenever it changes.
  }, []);
  // The welcome page's two hand-offs (ADR-036): `?signup=1` opens the sign-up form at once, and the
  // call to action that brought the person here is reported once — now on the first load rather
  // than on a consent click, because measurement no longer waits for one (founder, 2026-09-14).
  // Someone who has turned measurement off still reports nothing, and the stored value is read
  // rather than the render's state so a reload cannot report it twice.
  useEffect(() => {
    const params = new URLSearchParams(globalThis.location.search);
    if (params.get('signup') === '1') {
      setAuthMode('register');
      params.delete('signup');
      const rest = params.toString();
      globalThis.history.replaceState(
        null,
        '',
        `${globalThis.location.pathname}${rest === '' ? '' : `?${rest}`}`,
      );
    }
    if (preferences.analyticsOn()) reportLandingArrival();
    // Once, on mount: the address bar and the tab's memory are read when the page opens.
  }, []);
  const googleOffered = liveWiring?.signInMethods?.includes('google') === true;
  // `no_verification`: no relay in this deployment, so the account is made as given and the code
  // step is skipped (founder, 2026-09-13); a forgotten password still needs the relay.
  const registrationNeedsCode = liveWiring?.registration !== 'no_verification';
  // Its own signal since 2026-09-21: it was inferred from `registration`, so switching the sign-up
  // code off made this page call the reset unavailable on a host that could mail it.
  const resetUnavailable =
    liveWiring?.passwordReset === undefined
      ? liveWiring?.registration === 'no_verification'
      : liveWiring.passwordReset === 'unavailable';
  const linkOffered = liveWiring?.signInMethods?.includes('email_link') === true;
  // The resend button counts down while the code step is open; nothing else re-renders for it.
  useEffect(() => {
    if (registrationStep !== 'code' && resetStep !== 'code' && linkStep !== 'sent')
      return undefined;
    const timer = setInterval(() => {
      setResendTick((tick) => tick + 1);
    }, 1000);
    return () => {
      clearInterval(timer);
    };
  }, [registrationStep, resetStep, linkStep]);
  const resendInMs = resendAt === null ? 0 : Math.max(0, resendAt - Date.now());
  const canResend = resendInMs === 0;
  /** The details stated after the fact (ADR-034): an outside-door account, or one from before. */
  const saveProfile = async (): Promise<boolean> => {
    if (authBusy) return false;
    setError(null);
    const stated = statedDetailsOf(detailsValue, language);
    if (!stated.ok) {
      setError(stated.problem);
      return false;
    }
    setAuthBusy(true);
    try {
      const saved = await api.saveProfile(stated.details);
      if (!saved.ok) {
        setError(t.authErrors[saved.code] ?? t.failure.saveDetails);
        return false;
      }
      setAccountName(stated.details.displayName);
      // The closed choices, never the name: what the statistics are built on.
      analytics.identify(accountUserId, traitsOf(stated.details));
      // The card's first step saves the two details Bilal needs and offers the four it does not,
      // once; the second step — or a save from the settings — is the end of the card.
      if (needsProfile && profileStep === 'identity') setProfileStep('statistics');
      else {
        setNeedsProfile(false);
        setProfileStep('identity');
      }
      return true;
    } finally {
      setAuthBusy(false);
    }
  };
  /** The statistics are a person's to give or not: skipping them is a way in, not a refusal. */
  const skipStatistics = (): void => {
    setError(null);
    setNeedsProfile(false);
    setProfileStep('identity');
  };
  const appleOffered = liveWiring?.signInMethods?.includes('apple') === true;

  /** Everything after a door opens: the session, the identity, the event, the page. */
  const enterApp = async (
    userId: string,
    method: 'password' | 'email_link',
    event: 'signup_completed' | 'login',
    traits: Parameters<typeof analytics.identify>[1],
  ): Promise<void> => {
    setPassword('');
    setCode('');
    setRegistrationStep('details');
    setLinkStep('idle');
    setProfileStep('identity');
    const session = await api.resumeSession();
    const opened = session.ok ? session : await api.createSession();
    if (!opened.ok) {
      setError(t.failure.session);
      return;
    }
    setSessionId(opened.value.id);
    currentSession.id = opened.value.id;
    setMessages([]);
    setAccountEmail(email.trim().toLowerCase());
    setAccountUserId(userId);
    // The account by opaque id, never by email: the identity is what ties a flow together.
    analytics.identify(userId, traits);
    analytics.track(event, { method });
    // What the account already says about itself. Someone who signs in states nothing at the door,
    // so without this the greeting had no name until a reload and the settings opened on a blank
    // form. Registration passes its own traits above; this only fills what the server knows.
    const me = await api.me();
    if (me.ok) {
      setAccountEmail(me.value.email ?? email.trim().toLowerCase());
      setAccountName(me.value.profile?.displayName ?? null);
      setNeedsProfile(me.value.email !== null && me.value.profile === null);
      hydrateDetails(me.value.profile);
      if (traits === undefined) analytics.identify(userId, traitsOf(me.value.profile));
    }
    setPhase('ready');
    void refreshConversations();
  };
  /** Step two of registration (or the only step where no relay exists): the account is made. */
  const createAccount = async (codeGiven: string): Promise<void> => {
    setError(null);
    setAuthBusy(true);
    try {
      // An address and a password, and nothing else (ADR-041; founder, 2026-09-21: "we want sign
      // up to be dead easy"). The name and the form of address are asked once the person is in.
      const outcome = await api.register({
        email,
        password,
        code: codeGiven,
        adultAttested: true,
      });
      if (!outcome.ok) {
        setError(t.authErrors[outcome.code] ?? t.failure.generic);
        return;
      }
      setNeedsProfile(true);
      await enterApp(outcome.value.userId, 'password', 'signup_completed', undefined);
    } finally {
      setAuthBusy(false);
    }
  };
  /** Step one of registration (ADR-034): a code goes to the address; the account waits for it. */
  const startRegistration = async (): Promise<void> => {
    if (authBusy) return;
    setError(null);
    if (password.length < PASSWORD_MIN) {
      setError(t.authErrors['invalid_password'] ?? null);
      return;
    }
    if (!registrationNeedsCode) {
      await createAccount('');
      return;
    }
    setAuthBusy(true);
    try {
      const started = await api.startRegistration({ email });
      if (!started.ok) {
        setError(t.authErrors[started.code] ?? t.failure.sendCode);
        return;
      }
      setCode('');
      setResendAt(Date.now() + started.value.resendAfterMs);
      setRegistrationStep('code');
    } finally {
      setAuthBusy(false);
    }
  };
  /**
   * Signing in without a password (ADR-041): a single-use link goes to the address. This browser
   * remembers that it asked, so the link can finish by itself when it is opened here; anywhere
   * else it waits for one press, and a mail scanner that follows the link spends nothing.
   */
  const askForLink = async (): Promise<void> => {
    if (authBusy) return;
    setError(null);
    setAuthNotice(null);
    if (email.trim().length === 0) {
      setError(t.door.linkNeedsEmail);
      return;
    }
    setAuthBusy(true);
    try {
      const started = await api.startSignInLink({ email, adultAttested: true });
      if (!started.ok) {
        setError(t.authErrors[started.code] ?? t.failure.sendCode);
        return;
      }
      preferences.markLinkRequested();
      setResendAt(Date.now() + started.value.resendAfterMs);
      setLinkStep('sent');
    } finally {
      setAuthBusy(false);
    }
  };
  /** The link, opened: it signs the account in, or makes it and owes the two details. */
  const finishLink = async (token: string): Promise<void> => {
    setError(null);
    setAuthBusy(true);
    try {
      const outcome = await api.finishSignInLink({
        token,
        locale: language === 'en' ? 'en' : 'ar-SA',
      });
      preferences.clearLinkRequested();
      setPendingLink(null);
      if (!outcome.ok) {
        setError(t.authErrors[outcome.code] ?? t.failure.generic);
        return;
      }
      // Whoever opens a link has met the introduction where they asked for it.
      preferences.markOnboardingDone();
      setOnboarded(true);
      await enterApp(
        outcome.value.userId,
        'email_link',
        outcome.value.created ? 'signup_completed' : 'login',
        undefined,
      );
    } finally {
      setAuthBusy(false);
    }
  };
  /** A forgotten password, step one: the code goes to the address. */
  const startReset = async (): Promise<void> => {
    if (authBusy) return;
    setError(null);
    setAuthNotice(null);
    setAuthBusy(true);
    try {
      const started = await api.startPasswordReset({ email });
      if (!started.ok) {
        setError(t.authErrors[started.code] ?? t.failure.sendCode);
        return;
      }
      setCode('');
      setPassword('');
      setResendAt(Date.now() + started.value.resendAfterMs);
      setResetStep('code');
    } finally {
      setAuthBusy(false);
    }
  };
  /** Step two: the code and the new password; every old session ends, and the person signs in. */
  const finishReset = async (): Promise<void> => {
    if (authBusy) return;
    setError(null);
    if (password.length < PASSWORD_MIN) {
      setError(t.authErrors['invalid_password'] ?? null);
      return;
    }
    setAuthBusy(true);
    try {
      const done = await api.resetPassword({ email, code: code.trim(), password });
      if (!done.ok) {
        setError(t.authErrors[done.code] ?? t.failure.setPassword);
        return;
      }
      setPassword('');
      setCode('');
      setResetStep('email');
      setAuthMode('login');
      setAuthNotice(t.passwordSet);
    } finally {
      setAuthBusy(false);
    }
  };
  const submitAuth = async (): Promise<void> => {
    if (authBusy) return;
    if (authMode === 'reset') {
      await (resetStep === 'email' ? startReset() : finishReset());
      return;
    }
    if (authMode === 'register') {
      await (registrationStep === 'details' ? startRegistration() : createAccount(code.trim()));
      return;
    }
    setError(null);
    setAuthBusy(true);
    try {
      const outcome = await api.login({ email, password });
      if (!outcome.ok) {
        setError(t.authErrors[outcome.code] ?? t.failure.generic);
        return;
      }
      // An account from before the details existed, or made through an outside door, states
      // them once before the conversation.
      const me = await api.me();
      const profile = me.ok ? me.value.profile : null;
      setNeedsProfile(me.ok && me.value.email !== null && profile === null);
      setAccountName(profile?.displayName ?? null);
      await enterApp(outcome.value.userId, 'password', 'login', traitsOf(profile));
    } finally {
      setAuthBusy(false);
    }
  };
  const signOut = async (): Promise<void> => {
    await api.logout();
    analytics.track('logout');
    analytics.identify(null);
    setAccountEmail(null);
    setAccountName(null);
    setSettingsOpen(false);
    // The next person at this screen is signing in, whatever the last one did.
    setAuthMode('login');
    setPassword('');
    setLinkStep('idle');
    setSessionId(null);
    currentSession.id = null;
    setMessages([]);
    setDraft('');
    setPhase('signed_out');
  };

  const signIn = async (code?: string): Promise<void> => {
    setError(null);
    const submitted = (code ?? inviteCode).trim();
    // An empty code is refused at the door, without a server call to refuse it for us.
    if (submitted.length === 0) return;
    const signedIn = await api.signIn(submitted);
    if (!signedIn.ok) {
      setError(t.failure.developmentSignIn(signedIn.code));
      return;
    }
    const session = await api.createSession();
    if (!session.ok) {
      setError(t.failure.createSession(session.code));
      return;
    }
    setSessionId(session.value.id);
    currentSession.id = session.value.id;
    setPhase('ready');
  };

  /** A fresh conversation: a new server session, and the page starts over honestly from what it says. */
  const refreshConversations = async (): Promise<void> => {
    const listed = await api.listSessions();
    if (listed.ok) setConversations(listed.value.sessions);
  };

  /** Opens an existing conversation: its words re-render from the server's own log. */
  const openConversation = async (conversationId: string): Promise<void> => {
    setRailOpen(false);
    if (conversationId === sessionId) return;
    analytics.track('conversation_opened');
    setError(null);
    setSessionId(conversationId);
    currentSession.id = conversationId;
    const held = await api.conversation(conversationId);
    setMessages(
      held.ok
        ? held.value.entries.map((entry) =>
            entry.role === 'user'
              ? {
                  role: 'user' as const,
                  text: entry.text,
                  // A rebuilt act is shown as a choice, exactly as it was when they made it.
                  ...(entry.text.startsWith(INTERACTION_ACT_MARKER) ? { choice: true } : {}),
                }
              : {
                  role: 'assistant' as const,
                  operationId: null,
                  turn: {
                    turnId: '00000000-0000-4000-8000-000000000000',
                    assistantProse: entry.text,
                    sourceLinkProse: entry.sourceLinkProse ?? null,
                    sourceCards: entry.sourceCards ?? [],
                    interaction: entry.interaction ?? null,
                    safetyAction: 'none' as const,
                    degradeReason: null,
                    notice: null,
                  },
                },
          )
        : [],
    );
  };

  const newConversation = async (): Promise<void> => {
    setRailOpen(false);
    if (phase === 'sending') return;
    setError(null);
    const session = await api.createSession();
    if (!session.ok) {
      setError(t.failure.newConversation(session.code));
      return;
    }
    setSessionId(session.value.id);
    currentSession.id = session.value.id;
    setMessages([]);
    setDraft('');
    setSavedAfterTurn(null);
    setQueue([]);
    sessionEnded.current = false;
    analytics.track('conversation_created');
    void refreshConversations();
  };

  /**
   * Permanent erasure of one conversation, confirmed in words that carry the weight: the server
   * deletes the session, its stored turns, and the memory raised from them. Deleting the open
   * conversation leaves the person in a fresh one rather than in a dead view.
   */
  /**
   * Everything, at the person's own request. The confirmation names what goes and says the count
   * out loud, because "delete all" is worth being sure about and a number is harder to misread
   * than a promise.
   */
  const eraseEverything = async (): Promise<void> => {
    const count = conversations.length;
    if (!window.confirm(t.confirm.eraseConversations(count))) return;
    setError(null);
    const outcome = await api.eraseAllConversations();
    if (!outcome.ok) {
      setError(t.failure.erase(outcome.code));
      return;
    }
    analytics.track('conversations_erased', { count });
    await newConversation();
    void refreshConversations();
  };

  /** The profile only. A different request, and the wording says which. */
  const forgetProfile = async (): Promise<void> => {
    if (!window.confirm(t.confirm.forgetProfile)) return;
    setError(null);
    const outcome = await api.forgetProfile();
    if (!outcome.ok) {
      setError(t.failure.forget(outcome.code));
      return;
    }
    analytics.track('memory_forgotten');
    void refreshMemory();
  };

  /**
   * The whole account, at the person's request (ADR-032). The confirmation names everything that
   * goes; afterwards the page is a stranger's again, exactly as after sign-out.
   */
  const deleteAccount = async (): Promise<void> => {
    if (!window.confirm(t.confirm.deleteAccount)) return;
    setError(null);
    const outcome = await api.deleteAccount();
    if (!outcome.ok) {
      setError(t.failure.deleteAccount(outcome.code));
      return;
    }
    analytics.track('account_deleted');
    analytics.identify(null);
    setAuthMode('login');
    setEmail('');
    setPassword('');
    setSessionId(null);
    currentSession.id = null;
    setMessages([]);
    setDraft('');
    setConversations([]);
    setAccountEmail(null);
    setAccountName(null);
    setSettingsOpen(false);
    setPhase('signed_out');
  };

  const deleteConversation = async (conversationId: string): Promise<void> => {
    if (!window.confirm(t.confirm.deleteConversation)) return;
    setError(null);
    const outcome = await api.deleteSession(conversationId);
    if (!outcome.ok) {
      setError(t.failure.deleteConversation(outcome.code));
      return;
    }
    analytics.track('conversation_deleted');
    if (conversationId === sessionId) {
      await newConversation();
      return;
    }
    void refreshConversations();
  };

  const showExplore = (): void => {
    setRailOpen(false);
    setExploreOpen((current) => !current);
  };
  const showMemory = (): void => {
    setRailOpen(false);
    setMemoryOpen((current) => !current);
  };

  /** One panel at a time beneath the conversation: opening the settings folds the others away. */
  const showSettings = (): void => {
    setRailOpen(false);
    setExploreOpen(false);
    setMemoryOpen(false);
    setSettingsOpen((current) => !current);
  };

  /**
   * Two ways a recording ends on purpose. `review`: the words land in the box, editable, and the
   * person sends (BR-010/BR-011). `send`: the hold was released, and the words go as the turn — but
   * only when the transcription is sure of them; a doubtful one reviews instead, with a hint saying
   * why, because the one thing worse than typing is sending words you did not say.
   */
  type StopMode = 'review' | 'send';
  /** Below this, a released hold reviews rather than sends. */
  const SEND_CONFIDENCE = 0.8;
  const REVIEW_HINT = t.voice.reviewHint;
  const [voiceHint, setVoiceHint] = useState<string | null>(null);
  /** A hold released before the microphone even opened: record nothing rather than something unheard. */
  const releasedEarly = useRef(false);
  /** The realtime path ends through a callback; this carries the release's intent across to it. */
  const sendOnCommit = useRef(false);

  const startHttpRecording = async (): Promise<void> => {
    if (capture === undefined) {
      setVoice('off');
      setError(t.voice.liveUnavailable);
      return;
    }
    const started = await capture.start();
    if (started.kind === 'unavailable') {
      const reasons = {
        microphone_denied: t.voice.microphoneDenied,
        microphone_unavailable: t.voice.microphoneUnavailable,
        already_recording: t.voice.alreadyRecording,
      };
      setVoice('off');
      setError(reasons[started.reason]);
      // The control that was pressed is still there; leave the keyboard on it so it can be retried.
      return;
    }
    if (releasedEarly.current) {
      // The thumb lifted while the permission prompt was up. Nothing was heard; say nothing.
      releasedEarly.current = false;
      capture.cancel();
      setVoice('off');
      setFocusIntent('speak');
      return;
    }
    setVoice('recording');
    setFocusIntent('stop');
  };

  /**
   * A finished transcript. Reviewed, it lands in the composer — editable, sent by the person. Sent,
   * it joins whatever was already typed and goes as the turn, so nothing typed or spoken is lost.
   */ const acceptTranscript = (text: string, mode: StopMode): void => {
    if (text.trim().length === 0) {
      setError(t.voice.nothingHeard);
      setFocusIntent('speak');
      return;
    }
    analytics.track(mode === 'send' ? 'voice_recording_sent' : 'voice_recording_reviewed');
    if (mode === 'send') {
      submit(draft.trim().length === 0 ? text : `${draft} ${text}`, 'voice');
      composerBox.current?.focus();
      return;
    }
    setDraft((current) => (current.trim().length === 0 ? text : `${current} ${text}`));
    composerBox.current?.focus();
  };

  const startRecording = async (): Promise<void> => {
    setError(null);
    setVoiceHint(null);
    releasedEarly.current = false;
    sendOnCommit.current = false;
    if (sessionId === null) return;
    analytics.track('voice_recording_started', {
      mode: liveSeams === undefined ? 'recorded' : 'live',
    });
    if (liveSeams === undefined) {
      await startHttpRecording();
      return;
    }
    // Realtime first: words should appear while the person speaks. The HTTP recorder stays as the
    // documented fallback and takes over the moment the realtime path says it is unavailable.
    setVoice('recording');
    setLiveVoice({ committed: '', interim: '', level: 0, startedAt: Date.now(), live: false });
    setFocusIntent('stop');
    liveSession.current = startLiveTranscript(liveSeams, sessionId, {
      onLive: () => {
        // Usually already true: the microphone is heard from the press, before the relay is ready.
        setLiveVoice((v) =>
          v === null || v.live ? v : { ...v, live: true, startedAt: Date.now() },
        );
      },
      onCommitted: (text) => {
        setLiveVoice((v) => (v === null ? v : { ...v, committed: text }));
      },
      onInterim: (text) => {
        setLiveVoice((v) => (v === null ? v : { ...v, interim: text }));
      },
      onLevel: (level) => {
        // The first block means the microphone is open and what is said is being kept, relay ready
        // or not (2026-09-21) — so the interface says "I can hear you" from here, and the timer
        // starts here. Saying "Connecting…" while already listening only made people wait to speak.
        setLiveVoice((v) =>
          v === null
            ? v
            : v.live
              ? { ...v, level }
              : { ...v, level, live: true, startedAt: Date.now() },
        );
      },
      onEnded: (end) => {
        liveSession.current = null;
        setLiveVoice(null);
        if (end.kind === 'committed') {
          setVoice('off');
          // The realtime provider reports no confidence; its committed words are its final answer.
          acceptTranscript(end.transcript, sendOnCommit.current ? 'send' : 'review');
          return;
        }
        if (end.kind === 'cancelled') {
          setVoice('off');
          setError(null);
          setFocusIntent('speak');
          return;
        }
        if (end.kind === 'unavailable') {
          void startHttpRecording();
          return;
        }
        setVoice('off');
        setError(t.voice.liveFailed);
        setFocusIntent('speak');
      },
    });
    await Promise.resolve();
  };
  const stopRecording = async (requested: StopMode = 'review'): Promise<void> => {
    // The person's setting outranks the gesture: with auto-send off, a released hold reviews.
    const mode: StopMode = requested === 'send' && voiceAutoSend ? 'send' : 'review';
    if (liveSession.current !== null) {
      // The final transcript arrives through onEnded once the provider commits.
      sendOnCommit.current = mode === 'send';
      liveSession.current.stop();
      return;
    }
    if (capture === undefined) return;
    if (capture.state() !== 'recording') {
      // Released before the microphone answered (or after it refused). There is nothing to stop;
      // if it opens later, it closes again at once.
      if (mode === 'send') releasedEarly.current = true;
      return;
    }
    setVoice('transcribing');
    const outcome = await capture.stopAndTranscribe();
    setVoice('off');
    if (outcome.kind === 'unavailable') {
      setError(
        outcome.reason === 'no_audio_captured'
          ? t.voice.nothingRecorded
          : t.voice.transcriptionFailed,
      );
      setFocusIntent('speak');
      return;
    }
    if (mode === 'send' && outcome.confidence < SEND_CONFIDENCE) {
      setVoiceHint(REVIEW_HINT);
      acceptTranscript(outcome.text, 'review');
      return;
    }
    acceptTranscript(outcome.text, mode);
  };

  const cancelRecording = (): void => {
    if (liveSession.current !== null) {
      // The audio and every word are discarded; nothing further is sent.
      liveSession.current.cancel();
      return;
    }
    if (capture === undefined) return; // The audio is discarded without being sent anywhere; the controller destroys the buffer.
    capture.cancel();
    analytics.track('voice_recording_cancelled');
    setVoice('off');
    setError(null);
    setFocusIntent('speak');
  };

  /**
   * What pressing send does. The words go on screen and the box empties immediately either way; what
   * changes is whether this turn starts now or waits. A message written while a turn is running used
   * to be dropped without a word, which is the worst possible answer to someone still typing.
   */ const submit = (text: string, method: 'typed' | 'voice' = 'typed'): void => {
    if (sessionId === null || text.trim().length === 0) return;
    // The experience survey is mandatory (founder, 2026-09-20), and this is where that holds. The
    // composer deliberately stays open while a reply is in flight so messages can queue, so closing
    // a button would not close the door: typed text, Enter, a released hold and a chip all arrive
    // here, and none of them sends while the form is unanswered. The words stay in the box.
    if (survey !== null) return;
    const waiting = inFlight.current;
    setMessages((current) => [
      ...current,
      { role: 'user', text, ...(waiting ? { pending: true } : {}) },
    ]);
    setDraft('');
    setVoiceHint(null);
    analytics.track('message_sent', { method, queued: waiting });
    // The campaign conversion (item 9): the first message ever sent from this browser, once.
    if (!preferences.firstMessageRecorded()) {
      preferences.markFirstMessage();
      analytics.track('first_message_sent', { method });
    }
    const turn: TurnInput = { transcript: text, interaction: null };
    if (waiting) {
      setQueue((current) => [...current, turn]);
      return;
    }
    void commit(turn);
  };

  /**
   * Acting on an interaction (ADR-028). What reaches the model is the event — which interaction, which
   * option — and never a sentence put in the person's mouth: turning a tap on a label into "I choose
   * the amount of tasks" would file a click in the record as something they said.
   *
   * The label is shown in the conversation so the person can see what they picked, and is marked as a
   * choice rather than as typed words.
   */
  const chooseInteraction = (interactionId: string, optionId: string, label: string): void => {
    if (sessionId === null || interactionId === '') return;
    // Nothing sends while the experience survey is unanswered; see `submit`.
    if (survey !== null) return;
    setMessages((current) => [...current, { role: 'user', text: label, choice: true }]);
    const turn: TurnInput = {
      transcript: null,
      interaction: { interactionId, kind: 'option_selected', optionId },
    };
    if (inFlight.current) {
      setQueue((current) => [...current, turn]);
      return;
    }
    void commit(turn);
  };

  /** A sorted board is one event carrying every placement, not one event per tap. */
  const sortInteraction = (
    interactionId: string,
    placements: readonly { readonly optionId: string; readonly bucket: string }[],
  ): void => {
    if (sessionId === null || interactionId === '') return;
    // Nothing sends while the experience survey is unanswered; see `submit`.
    if (survey !== null) return;
    setMessages((current) => [...current, { role: 'user', text: t.chat.planSent, choice: true }]);
    const turn: TurnInput = {
      transcript: null,
      interaction: { interactionId, kind: 'sorted', placements },
    };
    if (inFlight.current) {
      setQueue((current) => [...current, turn]);
      return;
    }
    void commit(turn);
  };

  /**
   * A committed plan (ADR-028). One event carries the whole shape — the order they left it in, what
   * they removed, and which lines they rewrote — because a plan built a keystroke at a time would
   * be a conversation with a form.
   *
   * The lines they rewrote travel as their words and are marked as theirs. Everything else travels
   * as an identifier, so accepting a step Bilal proposed never becomes a sentence they "said".
   */
  const planInteraction = (interactionId: string, steps: readonly PlanStep[]): void => {
    if (sessionId === null || interactionId === '') return;
    // Nothing sends while the experience survey is unanswered; see `submit`.
    if (survey !== null) return;
    const kept = steps.filter((step) => !step.dropped);
    if (kept.length === 0) return;
    const dropped = steps.filter((step) => step.dropped).map((step) => step.id);
    const edited = kept.filter((step) => step.edited);
    const turn: TurnInput = {
      transcript: null,
      interaction: {
        interactionId,
        kind: 'plan_committed',
        order: kept.map((step) => step.id),
        dropped,
        edited: edited.map((step) => ({ optionId: step.id, text: step.label.trim() })),
      },
    };
    // Shown as what was sent, not as a first-person sentence they never typed.
    setMessages((current) => [
      ...current,
      {
        role: 'user',
        text: t.chat.planAsSent(
          kept.map((step, index) => `${String(index + 1)}. ${step.label.trim()}`).join('\n'),
        ),
        choice: true,
      },
    ]);
    if (inFlight.current) {
      setQueue((current) => [...current, turn]);
      return;
    }
    void commit(turn);
  };

  const skipInteraction = (interactionId: string): void => {
    if (sessionId === null || interactionId === '') return;
    // Nothing sends while the experience survey is unanswered; see `submit`.
    if (survey !== null) return;
    const turn: TurnInput = { transcript: null, interaction: { interactionId, kind: 'skipped' } };
    if (inFlight.current) {
      setQueue((current) => [...current, turn]);
      return;
    }
    void commit(turn);
  };

  /** Sends the next queued message, if the session is still one that can take a turn. */
  const drainQueue = (): void => {
    const [next, ...rest] = queueRef.current;
    if (next === undefined) return;
    setQueue(rest);
    // Its bubble is already on screen from when it was written; it stops being pending, not new.
    setMessages((current) => {
      const index = current.findIndex((entry) => entry.role === 'user' && entry.pending === true);
      if (index === -1) return current;
      const found = current[index];
      if (found?.role !== 'user') return current;
      const updated = [...current];
      updated[index] = { role: 'user', text: found.text };
      return updated;
    });
    void commit(next);
  };

  /** Nothing queued may be sent into a session that has ended; the person is told, not ignored. */
  const abandonQueue = (): void => {
    if (queueRef.current.length === 0) return;
    setQueue([]);
    setMessages((current) =>
      current.filter((entry) => !(entry.role === 'user' && entry.pending === true)),
    );
    setError(t.failure.closedConversation);
  };

  /**
   * Sends a turn, and does not make the person pay for an outage.
   *
   * A failure that means *the service could not answer* is retried here — the transport failing, an
   * operation that never produced a response, or a turn that came back degraded with an operational
   * code. Somebody who wrote something hard once should not have to write it twice because a
   * provider was busy, and before this they did: the outage arrived as a bubble in the conversation
   * and their words went back to the box for them to send again.
   *
   * A refusal is never retried. Nothing about their words was rejected in an outage, and nothing
   * about an outage is fixed by resending words that were.
   */
  // Exactly one. The adapter already retries once inside a turn (ADR-011), and the circuit breaker
  // opens on the third failed turn — so a second client attempt would spend the budget the breaker
  // keeps for deciding a provider is down, and punish the next message rather than this one.
  const RETRY_DELAYS_MS = [800];

  const commit = async (turn: TurnInput): Promise<void> => {
    const spoken = turn.transcript;
    if (sessionId === null || (spoken !== null && spoken.trim().length === 0)) return;
    if (spoken === null && turn.interaction === null) return;
    // Two rapid sends race React state; the ref refuses the second before state can catch up.
    if (inFlight.current) return;
    inFlight.current = true;
    if (turn.interaction !== null)
      analytics.track('interaction_used', { kind: turn.interaction.kind });
    setPhase('sending');
    setError(null);
    setRetryable(null);
    setStage('understanding');
    try {
      let delivered: TurnResponseDto | null = null;
      let operationId = '';
      let lastFailure = '';
      for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
        if (attempt > 0) {
          setStage('retrying');
          await new Promise((resolve) => {
            setTimeout(resolve, RETRY_DELAYS_MS[attempt - 1] ?? 700);
          });
          setStage('understanding');
        }
        const committed = await api.commitTurn(
          sessionId,
          turn,
          newClientTurnId(),
          null,
          depth,
          // The reading the interface is in. A reader who does not read Arabic is shown the verse
          // with a named provider edition's rendering of its meaning beside it.
          language,
        );
        if (!committed.ok) {
          lastFailure = committed.code;
          continue;
        }
        const operation = await awaitOperation(
          (id) => api.pollOperation(id),
          committed.value.id,
          setStage,
        );
        setStage(null);
        if (!operation.ok || operation.value.response === null) {
          lastFailure = operation.ok ? (operation.value.errorCode ?? 'unknown') : operation.code;
          continue;
        }
        const answered = operation.value.response;
        // A degraded turn records no question and stores no words, so re-sending costs the person
        // nothing and cannot duplicate their message in the conversation.
        if (answered.degradeReason !== null && RETRYABLE_DEGRADES.has(answered.degradeReason)) {
          lastFailure = answered.degradeReason;
          continue;
        }
        delivered = answered;
        operationId = committed.value.id;
        break;
      }
      setStage(null);
      if (delivered === null) {
        // Their words stay on screen, exactly where they wrote them, and one tap sends them again.
        setError(
          `${operationalMessage(lastFailure, language) || t.failure.service} (${lastFailure})`,
        );
        setRetryable(spoken);
        setPhase('ready');
        return;
      }
      // The canonical state machine gives a blocked session one exit — closed — so a crisis reply is
      // the end of this conversation, whatever is still queued behind it.
      if (
        delivered.safetyAction === 'crisis_script' ||
        delivered.safetyAction === 'decline_referral'
      )
        sessionEnded.current = true;
      const nextIndex = messages.length + 1;
      setMessages((current) => [...current, { role: 'assistant', turn: delivered, operationId }]);
      analytics.track('reply_received', {
        replyKind: delivered.replyKind ?? 'unknown',
        sourceCards: delivered.sourceCards.length,
        degraded: delivered.degradeReason !== null,
        safety: delivered.safetyAction,
      });
      if (delivered.sourceCards.length > 0)
        analytics.track('source_shown', { count: delivered.sourceCards.length });
      setPhase('ready');
      // Memory is automatic: when this turn saved something, a quiet chip says so — the mechanics
      // stay out of the conversation, and the profile itself lives behind the rail button.
      const before = factsCount.current;
      const after = await refreshMemory();
      // Never under an outage notice: a turn that could not be composed remembered nothing, and a
      // chip saying otherwise there is simply false.
      if (after > before && (delivered.degradeReason ?? null) === null)
        setSavedAfterTurn(nextIndex);
      void refreshConversations();
      // Asked after the reply, never before it: the person has just been answered, so the form is
      // never the first thing a message gets back. A failure here leaves the survey unasked rather
      // than blocking the conversation over a request that did not land.
      if (!sessionEnded.current) {
        const due = await api.surveyDue(sessionId);
        if (due.ok && due.value.due) setSurvey(due.value);
      }
    } finally {
      setStage(null);
      inFlight.current = false;
      // A crisis turn closes the session, and nothing queued may be sent into a closed one.
      if (sessionEnded.current) abandonQueue();
      else drainQueue();
    }
  };

  /** Speech for one delivered turn: named by operation id, playback level fed by the analyser. */
  const speechControlFor = (operationId: string) => ({
    operationId,
    speak: (id: string) => api.speakTurn(id),
    play: async (audio: SpokenAudio): Promise<void> => {
      // The level shown beside the reply is the playback's own: the analyser reports it and this
      // state carries it, nothing else.
      setSpeaking({ op: operationId, level: 0 });
      analytics.track('reply_listened');
      try {
        await playAudio(audio, (level) => {
          setSpeaking({ op: operationId, level });
        });
      } finally {
        setSpeaking(null);
      }
    },
  });

  const exitVoiceMode = (): void => {
    voiceLoop.current?.exit();
    voiceLoop.current = null;
    setVoiceMode(null);
    void refreshConversations();
  };

  const enterVoiceMode = (): void => {
    if (sessionId === null || liveSeams === undefined || voiceLoop.current !== null) return;
    setError(null);
    setVoiceMode({ kind: 'connecting' });
    voiceLoop.current = startVoiceLoop({
      language,
      startTranscript: (handlers, autoCommit) =>
        startLiveTranscript(liveSeams, sessionId, handlers, autoCommit),
      commitTurn: (transcript, clientTurnId) =>
        // A live conversation wants the fast lane: quick depth, whatever the composer selector says.
        api.commitTurn(
          sessionId,
          { transcript, interaction: null },
          clientTurnId,
          null,
          'quick',
          language,
        ),
      awaitOperation: (operationId, onStage) =>
        awaitOperation((id) => api.pollOperation(id), operationId, onStage),
      speak: (operationId, part) => api.speakTurn(operationId, part),
      play: (audio, onLevel, signal) => playAudio(audio, onLevel, signal),
      playRecitation: (url, onLevel, signal) => playRecitationUrl(url, onLevel, signal),
      noticeFor: (turn) => {
        if (turn.safetyAction === 'none') return null;
        // Exactly what the screen shows, from the same server-decided notice.
        return safetyText(turn);
      },
      onTurn: (userText, turn, operationId) => {
        setMessages((current) => [
          ...current,
          { role: 'user', text: userText },
          { role: 'assistant', turn, operationId },
        ]);
      },
      onState: (state) => {
        setVoiceMode(state);
        if (state.kind === 'ended' && state.reason === 'exited') setVoiceMode(null);
      },
      newClientTurnId,
      wait: (ms) =>
        new Promise((resolve) => {
          setTimeout(resolve, ms);
        }),
    });
  };

  const shellClass = ['app', railOpen ? 'rail-open' : '', railCollapsed ? 'rail-collapsed' : '']
    .filter((name) => name !== '')
    .join(' ');

  return (
    // Every component below reads the language from here rather than being handed it: the tree is
    // deep, and a prop threaded through fifteen components is a prop someone forgets.
    <LanguageContext.Provider value={language}>
      <main
        role="application"
        aria-label={t.appName}
        dir={direction}
        lang={language}
        className={shellClass}
      >
        {voiceMode !== null && <VoiceMode state={voiceMode} depth={depth} onExit={exitVoiceMode} />}
        <div className="content">
          {phase !== 'signed_out' && (
            <div className="topbar">
              <button
                type="button"
                className="icon-button menu-toggle"
                aria-label={t.menu}
                title={t.menu}
                onClick={() => {
                  const narrow = matchMediaOrNull?.('(max-width: 900px)').matches ?? false;
                  if (narrow) setRailOpen((current) => !current);
                  else setRailCollapsed(false);
                }}
              >
                ☰
              </button>
              <p className="topbar-title">{t.appName}</p>
              <span className="topbar-spacer" />
            </div>
          )}
          {/* Not while the settings are open: the dialog covers this, so the person saw an empty
            failure behind a scrim (founder, 2026-09-13). The dialog carries its own. */}
          {error !== null && !settingsOpen && (
            <p role="alert" className="failure-notice">
              <span>{error}</span>
              {retryable !== null && (
                <button
                  type="button"
                  className="retry-send"
                  onClick={() => {
                    const words = retryable;
                    setRetryable(null);
                    setError(null);
                    void commit({ transcript: words, interaction: null });
                  }}
                >
                  {t.retry}
                </button>
              )}
            </p>
          )}{' '}
          {phase === 'signed_out' && pendingLink !== null && (
            <div className="login-wrap">
              <div>
                <section aria-label={t.door.linkOpenTitle} className="login">
                  <LanguageSwitch language={language} onLanguage={chooseLanguage} />
                  <span className="logo-plate login-logo">
                    <img src="/brand/full-logo.svg" alt="" className="brand-light" />
                    <img src="/brand/full-logo-jass.svg" alt="" className="brand-dark" />
                  </span>
                  <h2>{t.door.linkOpenTitle}</h2>
                  <p className="login-hint">{t.door.linkOpenHint}</p>
                  {/* One press, on purpose: this browser did not ask for the link, and a scanner
                      that follows links does not press buttons — so it spends nothing. */}
                  <button
                    type="button"
                    disabled={authBusy}
                    onClick={() => void finishLink(pendingLink)}
                  >
                    {authBusy ? t.door.linkOpening : t.door.linkOpen}
                  </button>
                </section>
              </div>
            </div>
          )}
          {phase === 'signed_out' && pendingLink === null && !onboarded && (
            <Onboarding
              language={language}
              onLanguage={chooseLanguage}
              onFinished={() => {
                preferences.markOnboardingDone();
                setOnboarded(true);
              }}
              onEvent={(event, properties) => {
                analytics.track(event, properties);
              }}
            />
          )}
          {phase === 'signed_out' && pendingLink === null && onboarded && (
            <div className="login-wrap">
              <div>
                <section aria-label={t.door.region} className="login">
                  <LanguageSwitch language={language} onLanguage={chooseLanguage} />
                  <span className="logo-plate login-logo">
                    <img src="/brand/full-logo.svg" alt="" className="brand-light" />
                    <img src="/brand/full-logo-jass.svg" alt="" className="brand-dark" />
                  </span>
                  <h2>
                    {authMode === 'login'
                      ? t.door.welcomeBack
                      : authMode === 'reset'
                        ? t.door.resetTitle
                        : t.door.createTitle}
                  </h2>
                  <p className="login-hint">
                    {authMode === 'login'
                      ? t.door.signInHint
                      : authMode === 'reset'
                        ? t.door.resetHint
                        : t.door.createHint}
                  </p>
                  {authNotice !== null && (
                    <p className="auth-notice" role="status">
                      {authNotice}
                    </p>
                  )}
                  <p className="positioning">{t.positioning}</p>
                  {authMode !== 'reset' && (googleOffered || appleOffered) && (
                    <>
                      {/* A navigation, not a request: the browser goes to our own start route, which
                        sends it on to the provider. The attestation rides along only when given. */}
                      {googleOffered && (
                        <a
                          className="button-google"
                          href={`${baseUrl}/v1/auth/google/start?adultAttested=1`}
                        >
                          <svg
                            className="provider-logo"
                            viewBox="0 0 48 48"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path
                              fill="#EA4335"
                              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                            />
                            <path
                              fill="#4285F4"
                              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                            />
                            <path
                              fill="#34A853"
                              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                            />
                          </svg>
                          <span>{t.door.google}</span>
                        </a>
                      )}
                      {appleOffered && (
                        <a
                          className="button-google button-apple"
                          href={`${baseUrl}/v1/auth/apple/start?adultAttested=1`}
                        >
                          <svg
                            className="provider-logo"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path
                              fill="currentColor"
                              d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"
                            />
                          </svg>
                          <span>{t.door.apple}</span>
                        </a>
                      )}
                      <p className="or-divider" aria-hidden="true">
                        {t.door.or}
                      </p>
                    </>
                  )}
                  {authMode === 'reset' ? (
                    resetUnavailable ? (
                      <p className="door-closed" role="status">
                        {t.door.resetUnavailable}
                      </p>
                    ) : resetStep === 'email' ? (
                      <>
                        <label htmlFor="auth-email">{t.door.email}</label>
                        <input
                          id="auth-email"
                          type="email"
                          dir="ltr"
                          autoComplete="email"
                          value={email}
                          onChange={(event) => {
                            setEmail(event.target.value);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') void submitAuth();
                          }}
                        />
                        <button type="button" disabled={authBusy} onClick={() => void submitAuth()}>
                          {t.door.sendCode}
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="code-note">
                          {t.door.codeSentReset(email.trim().toLowerCase())}
                        </p>
                        <label htmlFor="auth-code">{t.door.code}</label>
                        <input
                          id="auth-code"
                          className="code-input"
                          type="text"
                          inputMode="numeric"
                          autoComplete="one-time-code"
                          maxLength={6}
                          value={code}
                          onChange={(event) => {
                            setCode(event.target.value.replace(/\D/gu, '').slice(0, 6));
                          }}
                        />
                        <label htmlFor="auth-password">{t.door.newPassword}</label>
                        <input
                          id="auth-password"
                          type="password"
                          dir="ltr"
                          autoComplete="new-password"
                          value={password}
                          onChange={(event) => {
                            setPassword(event.target.value);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') void submitAuth();
                          }}
                        />
                        <button
                          type="button"
                          disabled={authBusy || code.length !== 6}
                          onClick={() => void submitAuth()}
                        >
                          {t.door.setPassword}
                        </button>
                        <div className="code-actions">
                          <button
                            type="button"
                            disabled={authBusy || !canResend}
                            onClick={() => void startReset()}
                          >
                            {canResend
                              ? t.door.resendCode
                              : t.door.resendIn(Math.ceil(resendInMs / 1000))}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setError(null);
                              setResetStep('email');
                            }}
                          >
                            {t.door.editEmail}
                          </button>
                        </div>
                      </>
                    )
                  ) : linkStep === 'sent' ? (
                    <>
                      <p className="code-note" role="status">
                        {t.door.linkSent(email.trim().toLowerCase())}
                      </p>
                      <div className="code-actions">
                        <button
                          type="button"
                          disabled={authBusy || !canResend}
                          onClick={() => void askForLink()}
                        >
                          {canResend
                            ? t.door.linkResend
                            : t.door.resendIn(Math.ceil(resendInMs / 1000))}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setError(null);
                            setLinkStep('idle');
                          }}
                        >
                          {t.door.editEmail}
                        </button>
                      </div>
                    </>
                  ) : authMode === 'register' && registrationStep === 'code' ? (
                    <>
                      <p className="code-note">
                        {t.door.codeSentRegister(email.trim().toLowerCase())}
                      </p>
                      <label htmlFor="auth-code">{t.door.code}</label>
                      <input
                        id="auth-code"
                        className="code-input"
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={code}
                        onChange={(event) => {
                          setCode(event.target.value.replace(/\D/gu, '').slice(0, 6));
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') void submitAuth();
                        }}
                      />
                      <button
                        type="button"
                        disabled={authBusy || code.length !== 6}
                        onClick={() => void submitAuth()}
                      >
                        {t.door.createAccount}
                      </button>
                      <div className="code-actions">
                        <button
                          type="button"
                          disabled={authBusy || !canResend}
                          onClick={() => void startRegistration()}
                        >
                          {canResend
                            ? t.door.resendCode
                            : t.door.resendIn(Math.ceil(resendInMs / 1000))}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setError(null);
                            setRegistrationStep('details');
                          }}
                        >
                          {t.door.editEmail}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <label htmlFor="auth-email">{t.door.email}</label>
                      <input
                        id="auth-email"
                        type="email"
                        dir="ltr"
                        autoComplete="email"
                        value={email}
                        onChange={(event) => {
                          setEmail(event.target.value);
                        }}
                      />
                      <label htmlFor="auth-password">{t.door.passwordLabel}</label>
                      <input
                        id="auth-password"
                        type="password"
                        dir="ltr"
                        autoComplete={authMode === 'login' ? 'current-password' : 'new-password'}
                        value={password}
                        onChange={(event) => {
                          setPassword(event.target.value);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') void submitAuth();
                        }}
                      />
                      <button type="button" disabled={authBusy} onClick={() => void submitAuth()}>
                        {authMode === 'login'
                          ? t.door.signIn
                          : registrationNeedsCode
                            ? t.door.sendCode
                            : t.door.createAccount}
                      </button>
                      {linkOffered && (
                        <button
                          type="button"
                          className="auth-link"
                          disabled={authBusy}
                          onClick={() => void askForLink()}
                        >
                          {t.door.linkAsk}
                        </button>
                      )}
                      {authMode === 'login' && (
                        <button
                          type="button"
                          className="auth-switch forgot"
                          onClick={() => {
                            setError(null);
                            setAuthNotice(null);
                            setResetStep('email');
                            setCode('');
                            setAuthMode('reset');
                          }}
                        >
                          {t.door.forgot}
                        </button>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    className="auth-switch"
                    onClick={() => {
                      setError(null);
                      setAuthNotice(null);
                      setRegistrationStep('details');
                      setResetStep('email');
                      setLinkStep('idle');
                      setCode('');
                      setAuthMode((current) => (current === 'login' ? 'register' : 'login'));
                    }}
                  >
                    {authMode === 'login'
                      ? t.door.toRegister
                      : authMode === 'reset'
                        ? t.door.backToSignIn
                        : t.door.toSignIn}
                  </button>
                  {liveWiring !== null && liveWiring.providerMode === 'fake' && (
                    <button
                      type="button"
                      className="quick-dev"
                      onClick={() => void signIn(DEVELOPMENT_INVITE)}
                    >
                      {t.door.developmentEntry}
                    </button>
                  )}
                  {/* The attestation the intended use requires, as the sentence every door shows
                    instead of a box to tick (founder, 2026-09-12). Registration and both outside
                    doors carry it; the server still records the act and its policy version. */}
                  <p className="legal-links">
                    {t.door.attestationBefore}
                    <a href="/terms">{t.door.attestationTerms}</a>
                    {t.door.attestationAnd}
                    <a href="/privacy">{t.door.attestationPrivacy}</a>
                    {t.door.attestationAfter}
                  </p>
                </section>
              </div>{' '}
            </div>
          )}
          {phase !== 'signed_out' && needsProfile && (
            <div className="login-wrap">
              <div>
                <section aria-label={t.profileCard.region} className="login">
                  <LanguageSwitch language={language} onLanguage={chooseLanguage} />
                  {profileStep === 'identity' ? (
                    <>
                      <h2>{t.profileCard.title}</h2>
                      <p className="login-hint">{t.profileCard.hint}</p>
                      <DetailsFields
                        idPrefix="profile"
                        fields="identity"
                        value={detailsValue}
                        onChange={changeDetails}
                      />
                      <button type="button" disabled={authBusy} onClick={() => void saveProfile()}>
                        {t.profileCard.submit}
                      </button>
                    </>
                  ) : (
                    <>
                      <h2>{t.profileCard.moreTitle}</h2>
                      <p className="login-hint">{t.profileCard.moreHint}</p>
                      <DetailsFields
                        idPrefix="profile"
                        fields="statistics"
                        value={detailsValue}
                        onChange={changeDetails}
                      />
                      <button type="button" disabled={authBusy} onClick={() => void saveProfile()}>
                        {t.profileCard.save}
                      </button>
                      {/* As easy to find as the button above it: an optional question whose way
                          out is hidden is not optional. */}
                      <button type="button" className="auth-link" onClick={skipStatistics}>
                        {t.profileCard.skip}
                      </button>
                    </>
                  )}
                </section>
              </div>
            </div>
          )}
          {phase !== 'signed_out' && !needsProfile && (
            <section
              aria-label={t.chat.session}
              className={messages.length === 0 && phase === 'ready' ? 'chat empty' : 'chat'}
            >
              {messages.length === 0 && phase === 'ready' ? null : (
                <div
                  className="messages-scroll"
                  tabIndex={0}
                  role="group"
                  aria-label={t.chat.scroll}
                >
                  <ol aria-label={t.chat.list} className="messages">
                    {messages.map((message, index) => (
                      <li
                        key={index}
                        className={
                          message.role === 'user'
                            ? message.pending === true
                              ? 'bubble user pending'
                              : message.choice === true
                                ? 'bubble user choice'
                                : 'bubble user'
                            : 'bubble assistant'
                        }
                      >
                        {message.role === 'user' ? (
                          <p className="user-text" dir="auto">
                            {message.text}
                          </p>
                        ) : (
                          <AssistantResponse
                            turn={message.turn}
                            entrance={index === messages.length - 1}
                            speakingLevel={
                              speaking !== null && speaking.op === message.operationId
                                ? speaking.level
                                : null
                            }
                            speech={
                              // Speech is offered on the latest reply only: one control, for the words most
                              // likely to be listened to, named by operation id and never by text.
                              index === messages.length - 1 && message.operationId !== null
                                ? speechControlFor(message.operationId)
                                : null
                            }
                          />
                        )}
                        {message.role === 'assistant' &&
                          index === messages.length - 1 &&
                          (message.turn.questions?.length ?? (message.turn.question ? 1 : 0)) >
                            0 && <p className="waiting-line">{t.chat.waiting}</p>}
                        {message.role === 'assistant' &&
                          message.turn.interaction != null &&
                          index === messages.length - 1 && (
                            <InteractionPanel
                              interaction={message.turn.interaction}
                              busy={phase === 'sending'}
                              onChoose={(optionId, label) => {
                                chooseInteraction(
                                  message.turn.interaction?.id ?? '',
                                  optionId,
                                  label,
                                );
                              }}
                              onSort={(placements) => {
                                sortInteraction(message.turn.interaction?.id ?? '', placements);
                              }}
                              onPlan={(steps) => {
                                planInteraction(message.turn.interaction?.id ?? '', steps);
                              }}
                              onSkip={() => {
                                skipInteraction(message.turn.interaction?.id ?? '');
                              }}
                            />
                          )}
                        {message.role === 'assistant' && savedAfterTurn === index && (
                          <p className="memory-saved">{t.chat.memorySaved}</p>
                        )}
                      </li>
                    ))}
                    {phase === 'sending' && (
                      <li className="bubble assistant">
                        <p role="status" className="thinking">
                          <BilalMark state="thinking" depth={depth} size={26} />
                          <span key={stage ?? 'understanding'} className="stage-line">
                            {stageLine(stage, language)}
                          </span>
                        </p>
                      </li>
                    )}
                    <li ref={scrollEnd} aria-hidden="true" />
                  </ol>
                </div>
              )}
              {messages.length === 0 && phase === 'ready' && <EmptyGreeting name={accountName} />}
              {exploreOpen && (
                <ExplorePanel
                  search={(query) => api.exploreQuran(query)}
                  onClose={() => {
                    setExploreOpen(false);
                  }}
                />
              )}
              {memoryOpen && (
                <section aria-label={t.memory.region} className="memory" ref={memorySection}>
                  <h2>{t.memory.title}</h2>
                  {memory.facts.length === 0 ? (
                    <p className="memory-empty">{t.memory.empty}</p>
                  ) : (
                    <ul className="memory-facts" aria-label={t.memory.list}>
                      {memory.facts.map((fact) => (
                        <li key={fact.id} dir="auto">
                          {fact.key} — {fact.value}
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              )}{' '}
              {survey !== null && (
                <SurveyForm
                  survey={survey}
                  language={language}
                  onSubmit={async (answer) => {
                    if (sessionId === null) return false;
                    const stored = await api.answerSurvey(sessionId, {
                      surveyVersion: survey.surveyVersion,
                      ...answer,
                    });
                    if (!stored.ok) return false;
                    analytics.track('survey_answered', { wouldPay: answer.wouldPay });
                    setSurvey(null);
                    return true;
                  }}
                />
              )}
              {settingsOpen && (
                <Settings
                  theme={theme}
                  onTheme={chooseTheme}
                  voiceAutoSend={voiceAutoSend}
                  onVoiceAutoSend={chooseVoiceAutoSend}
                  analyticsConsent={analyticsConsent}
                  onAnalyticsConsent={chooseAnalyticsConsent}
                  languageChoice={languageChoice}
                  onLanguage={chooseLanguage}
                  details={detailsValue}
                  onDetailsChange={changeDetails}
                  onSaveProfile={saveProfile}
                  savingProfile={authBusy}
                  accountEmail={accountEmail}
                  onOpenMemory={() => {
                    setSettingsOpen(false);
                    setMemoryOpen(true);
                  }}
                  onForgetProfile={() => void forgetProfile()}
                  onEraseConversations={() => void eraseEverything()}
                  conversationCount={conversations.length}
                  onSignOut={() => void signOut()}
                  onDeleteAccount={() => void deleteAccount()}
                  notice={error}
                  onClose={() => {
                    setError(null);
                    setSettingsOpen(false);
                  }}
                />
              )}
              {/* Measurement now starts by itself (founder, 2026-09-14), so this line is a notice
                rather than a question: it says what is already running and offers the way out in
                the same breath. Both buttons stay the same size and the "off" one is not hidden or
                faded — an opt-out nobody can find is not an opt-out — and `/privacy` was rewritten
                in the same commit, because a published page that says nothing is measured until
                you answer must not survive the day measurement stopped waiting for an answer.
                Whether an implied consent of this shape satisfies PDPL and GDPR for product
                analytics is counsel's call and is recorded as NA-11j. */}
              {analytics.configured && analyticsConsent === null && (
                <div role="region" aria-label={t.consent.region} className="consent-bar">
                  <p>
                    {t.consent.line}{' '}
                    <a href="/privacy" target="_blank" rel="noreferrer">
                      {t.consent.details}
                    </a>
                  </p>
                  <div className="consent-actions">
                    <button
                      type="button"
                      className="button-primary"
                      onClick={() => {
                        chooseAnalyticsConsent('granted');
                      }}
                    >
                      {t.consent.acknowledge}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        chooseAnalyticsConsent('denied');
                      }}
                    >
                      {t.consent.turnOff}
                    </button>
                  </div>
                </div>
              )}
              <Composer
                draft={draft}
                onDraftChange={setDraft}
                onSend={() => {
                  submit(draft);
                }}
                sending={phase === 'sending'}
                voice={voice}
                canRecord={capture !== undefined || liveSeams !== undefined}
                onStartRecording={() => void startRecording()}
                onStopRecording={() => void stopRecording('review')}
                onReleaseToSend={() => void stopRecording('send')}
                onCancelRecording={cancelRecording}
                hint={voiceHint}
                speakButton={speakButton}
                live={liveVoice}
                onEnterVoiceMode={
                  liveSeams !== undefined && realtimeVoiceEnabled ? enterVoiceMode : undefined
                }
                stopButton={stopButton}
                boxRef={composerBox}
              />
              {messages.length === 0 && phase === 'ready' && (
                <EmptyExamples onPick={setDraft} onExplore={showExplore} />
              )}
            </section>
          )}
        </div>
        {phase !== 'signed_out' && (
          <Sidebar
            open={railOpen}
            onClose={() => {
              setRailOpen(false);
            }}
            onCollapse={() => {
              setRailCollapsed(true);
            }}
            onNewConversation={() => void newConversation()}
            onShowMemory={showMemory}
            onShowExplore={showExplore}
            onShowSettings={showSettings}
            accountLabel={accountEmail}
            accountName={accountName}
            theme={theme}
            onToggleTheme={toggleTheme}
            liveIndicator={null}
            onSignOut={() => void signOut()}
            conversations={conversations}
            activeConversationId={sessionId}
            onOpenConversation={(id) => void openConversation(id)}
            onDeleteConversation={(id) => void deleteConversation(id)}
          />
        )}
      </main>
    </LanguageContext.Provider>
  );
}
